import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Splash } from '@ofself/shell';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NucleusSplashMark } from './components/nucleus/NucleusMark';
import { GroupProvider, useGroup } from './contexts/GroupContext';
import { RoutedNotificationProvider } from './contexts/NotificationContext';
import { SavedViewsProvider } from './contexts/SavedViewsContext';
import { ToastProvider } from './components/common';
import Login from './pages/Login';
import Landing from './pages/Landing';
import JoinPhoto from './pages/join/JoinPhoto';
import JoinCodes from './pages/join/JoinCodes';
import JoinPending from './pages/join/JoinPending';
import JoinPhone from './pages/join/JoinPhone';
import { joinInFlight } from './utils/joinFlow';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import GraphView from './pages/GraphView';
import PreviewRoute from './pages/nucleus/PreviewRoute';
import GrantRoute from './pages/grant/GrantRoute';
import ConnectRoute from './pages/grant/ConnectRoute';
import ConnectMacRoute from './pages/grant/ConnectMacRoute';
import ConnectBuilderRoute from './pages/grant/ConnectBuilderRoute';
import ConnectPhoneRoute from './pages/grant/ConnectPhoneRoute';
import RatifyRoute from './pages/grant/RatifyRoute';
import PaidReturnRoute from './pages/grant/PaidReturnRoute';
import Handoff from './pages/Handoff';
import Logout from './pages/Logout';
import PluginDoor from './pages/plugins/PluginDoor';
import GroupPublicView from './pages/group/GroupPublicView';
import './styles/App.css';
import Layout from './components/Layout';
import HomeMap from './pages/home/HomeMap';
import HelpPage from './pages/help/HelpPage';
import { SECTION_PAGES } from './pages/sections';
import { SECTIONS } from './nucleus/map';
import { legacyTarget } from './nucleus/legacy';
import ErrorBoundary from './components/ErrorBoundary';
import GroupGuard from './components/nucleus/GroupGuard';
import { loginUrlFor } from './utils/loginRedirect';
import { getAppPreview } from './utils/appPreview';

// Protected route component
function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading, hasAccess } = useAuth();
  const location = useLocation();

  // Only a COLD boot (no cached identity) ever waits here now, and it waits
  // on bare stage — the beige "Loading…" wall is retired (owner call
  // 2026-08-22). A warm load hydrates from the auth snapshot and never
  // passes through this branch.
  if (loading) {
    return null;
  }

  if (!user) {
    // Carries the address being bounced from, so login returns here instead of
    // depositing everyone on /graph. utils/loginRedirect.js.
    return <Navigate to={loginUrlFor(location)} replace />;
  }

  if (!hasAccess('app')) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', fontFamily: 'var(--font-mono, monospace)' }}>
        <h2 style={{ marginBottom: '1rem' }}>Access Denied</h2>
        <p style={{ opacity: 0.6 }}>Your account does not have app access. Contact an administrator.</p>
      </div>
    );
  }

  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/home" />;
  }

  // A tab holding an app-catalog preview (set by GraphView.js on arrival via
  // the dev-portal handoff) is scoped to that one app's /app/:id/* routes for
  // its whole lifetime — this is the actual boundary, not just a hidden nav
  // item: typing /self or resuming a tab left open on another route bounces
  // straight back to the catalog. See appPreview.js and
  // decision_designs_ledger/2026-09-24_entity-scoped-nucleus-nav.md.
  const preview = getAppPreview();
  if (preview && !location.pathname.startsWith(`/app/${preview.id}/`)) {
    return <Navigate to={`/app/${preview.id}/graph`} replace />;
  }

  return children;
}

// Public route (redirect if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  // Sign-up signs the new account in before moving to its next step; that
  // moment is not a signed-in visitor to send home (utils/joinFlow).
  if (user && !joinInFlight()) {
    return <Navigate to="/home" />;
  }

  return children;
}

// THE SPLASH: the kit's cold-load intro, drawing the Nucleus mark in from the
// centre outward (components/nucleus/marks/nucleus.js), then fading to the app. Mounted once at the
// root on the authed path only: the login screens are not the app.
function AuthedSplash() {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return <Splash app="nucleus" mark={<NucleusSplashMark />} grid={false} />;
}

// AN OLD ADDRESS (nucleus/legacy.js) lands on the page that now holds the same
// thing. /trash and /proposals could never name a space, so they belong to the
// space you are standing in: they wait one render for the remembered space.
export function Legacy({ followSpace = false }) {
  const location = useLocation();
  const { activeGroupId } = useGroup();
  const [ready, setReady] = React.useState(!followSpace);
  React.useEffect(() => { setReady(true); }, []);
  if (!ready) return null;
  const q = new URLSearchParams(location.search);
  if (followSpace && activeGroupId && !q.get('group_id')) q.set('group_id', activeGroupId);
  const search = q.toString() ? `?${q.toString()}` : '';
  return <Navigate to={legacyTarget(location.pathname, search) || '/home'} replace />;
}

// A SECTION PAGE: the map's address, unless the query still speaks an old
// dialect (?app=, ?tab=, ?pane=), which is rewritten first. In a group, a group
// you are not in takes you out (GroupGuard).
function SectionRoute({ id }) {
  const location = useLocation();
  const to = legacyTarget(location.pathname, location.search);
  if (to) return <Navigate to={to} replace />;
  const Page = SECTION_PAGES[id];
  return <ProtectedRoute><Layout><GroupGuard><Page /></GroupGuard></Layout></ProtectedRoute>;
}

function AppCatalogGraphView() {
  const { appId } = useParams();
  return <GraphView key={appId} />;
}

function App() {
  return (
    <ErrorBoundary>
    <ToastProvider>
      <Router>
        <AuthProvider>
          <GroupProvider>
          <RoutedNotificationProvider>
          <SavedViewsProvider>
            <div className="app">
              <AuthedSplash />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/handoff" element={<Handoff />} />
                <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                {/* JOIN — the steps after the account: the phone that approves it
                    (SIGNUP_MODE=phone; signed out until it is proved), a photo,
                    the recovery codes (pages/join, utils/joinFlow). */}
                <Route path="/join/phone" element={<JoinPhone />} />
                <Route path="/join/photo" element={<ProtectedRoute><JoinPhoto /></ProtectedRoute>} />
                <Route path="/join/codes" element={<ProtectedRoute><JoinCodes /></ProtectedRoute>} />
                <Route path="/join/pending" element={<JoinPending />} />

                {/* Protected routes */}
                {/* THE FRONT DOOR — the page behind the column's Nu tile,
                    and where a login lands. */}
                <Route path="/home" element={<ProtectedRoute><Layout><HomeMap /></Layout></ProtectedRoute>} />
                {/* HELP — every concept, one page (pages/help): the dock's Help, Quick Help's
                    docs door and the search land here */}
                <Route path="/help" element={<ProtectedRoute><Layout><HelpPage /></Layout></ProtectedRoute>} />
                <Route path="/group/:groupId/help" element={<ProtectedRoute><Layout><HelpPage /></Layout></ProtectedRoute>} />
                {import.meta.env.DEV && <Route path="/__preview/:page" element={<ProtectedRoute><Layout><PreviewRoute /></Layout></ProtectedRoute>} />}

                {/* THE MAP (nucleus/map.js): every section at /<section>[/<page>],
                    and in a group at /group/<id>/<section>[/<page>]. */}
                {SECTIONS.flatMap((sec) => [
                  <Route key={sec.id} path={`/${sec.id}`} element={<SectionRoute id={sec.id} />} />,
                  <Route key={`${sec.id}-p`} path={`/${sec.id}/:page`} element={<SectionRoute id={sec.id} />} />,
                  <Route key={`${sec.id}-g`} path={`/group/:groupId/${sec.id}`} element={<SectionRoute id={sec.id} />} />,
                  <Route key={`${sec.id}-gp`} path={`/group/:groupId/${sec.id}/:page`} element={<SectionRoute id={sec.id} />} />,
                ])}

                {/* Pages that stand outside the map's grammar */}
                <Route path="/app/:appId/graph" element={<ProtectedRoute><Layout><AppCatalogGraphView /></Layout></ProtectedRoute>} />
                <Route path="/group/:groupId/public" element={<ProtectedRoute><Layout><GroupPublicView /></Layout></ProtectedRoute>} />
                <Route path="/me/public" element={<ProtectedRoute><Layout><GroupPublicView /></Layout></ProtectedRoute>} />
                {/* A PLUGIN'S OLD DOORS, which apps and the OAuth callback still name:
                    the Plugins page of that space, the plugin open in its inspector
                    (pages/plugins/PluginDoor.js) */}
                <Route path="/plugins/:pluginId/setup" element={<ProtectedRoute><PluginDoor /></ProtectedRoute>} />
                <Route path="/plugins/:pluginId/consent" element={<ProtectedRoute><PluginDoor /></ProtectedRoute>} />

                {/* Old addresses: notifications, redirect_uris and bookmarks */}
                {['/privacy-realms', '/exposure-profiles', '/applications', '/authorized-apps', '/billing', '/automations',
                  '/manage', '/inbox', '/history', '/schemas', '/settings', '/access', '/access/*',
                  '/group/:groupId', '/group/:groupId/*'].map((path) => (
                  <Route key={path} path={path} element={<ProtectedRoute><Legacy /></ProtectedRoute>} />
                ))}
                <Route path="/trash" element={<ProtectedRoute><Legacy followSpace /></ProtectedRoute>} />
                <Route path="/proposals" element={<ProtectedRoute><Legacy followSpace /></ProtectedRoute>} />

                <Route path="/grant" element={<GrantRoute />} />
                {/* Ratification is not a grant — its own page, same library. */}
                {/* Back from Stripe. Verifies the payment, then completes the grant
                    the buyer already decided on before they left. */}
                <Route path="/grant/paid" element={<PaidReturnRoute />} />
                <Route path="/grant/ratify" element={<RatifyRoute />} />

                {/* LOGGING IN IS NOT AUTHORIZING, and now has its own address.
                    /connect never shows a screen and /authorize always does, so
                    an app asking "who is this person" no longer has to ask
                    "will you approve this app" to find out. It falls through to
                    /authorize whenever there is something real to ask. */}
                <Route path="/connect" element={<ConnectRoute />} />
                {/* Nucleus on the Mac signs in here: asks, then hands a PKCE-bound
                    code to 127.0.0.1 only. Protected, so a signed-out person logs
                    in first and lands back on the question. */}
                <Route path="/connect-mac" element={<ProtectedRoute><ConnectMacRoute /></ProtectedRoute>} />
                {/* dev.oS and the paradigm CLI, the same way, for a narrower session. */}
                <Route path="/connect-builder" element={<ProtectedRoute><ConnectBuilderRoute /></ProtectedRoute>} />
                <Route path="/connect-phone" element={<ProtectedRoute><ConnectPhoneRoute /></ProtectedRoute>} />

                {/* /authorize* IS the grant page — a permanent second name, not
                    a deprecated one. Apps, the SDK and the docs build these URLs
                    in their own source; they should never have to be edited, and
                    a redirect would add a render and rewrite the address bar for
                    no benefit. Same component, same single preview fetch. */}
                <Route path="/authorize" element={<GrantRoute />} />
                <Route path="/authorize-user" element={<GrantRoute />} />
                <Route path="/authorize-group" element={<GrantRoute />} />
                <Route path="/authorize-follow" element={<GrantRoute />} />

                {/* Default route */}
                <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
                <Route path="*" element={<Navigate to="/home" />} />
              </Routes>
            </div>
          </SavedViewsProvider>
          </RoutedNotificationProvider>
          </GroupProvider>
        </AuthProvider>
      </Router>
    </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
